import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get('/api/auth/me');
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
      return null;
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post('/api/auth/login', { username, password });
      const data = response.data;
      // Handle both { token, user } and { data: { token, user } } response shapes
      const payload = data && typeof data === 'object' ? (data.data || data) : null;
      const newToken = payload?.token;
      const userData = payload?.user;
      if (!newToken || !userData || typeof userData !== 'object' || !userData.role) {
        console.error('Login: invalid response shape', {
          dataType: typeof data,
          hasUser: !!userData,
          userDataType: typeof userData,
          dataSample: typeof data === 'string' ? data.substring(0, 100) : JSON.stringify(data)?.substring(0, 200),
        });
        return { success: false, error: 'Invalid server response. Check console for details.' };
      }
      setToken(newToken);
      setUser(userData);
      localStorage.setItem('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      return { success: true, user: userData };
    } catch (error) {
      const status = error.response?.status;
      const msg = error.response?.data?.error;
      const networkErr = error.code === 'ERR_NETWORK' || error.message?.includes('Network Error');
      let userMsg = msg || 'Login failed';
      if (networkErr) userMsg = 'Cannot reach server. Is the backend running on port 5001?';
      else if (status === 401) userMsg = msg || 'Invalid credentials';
      else if (status >= 500) userMsg = 'Server error. Check backend logs.';
      console.error('Login failed:', { status, msg, networkErr, error: error.message });
      return { success: false, error: userMsg };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  // Set auth from registration or other external source (e.g. token + user from register-parent)
  const setAuth = (newToken, userData) => {
    if (!newToken || !userData?.role) return;
    setToken(newToken);
    setUser(userData);
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  // Expose fetchUser so components can refresh user data (e.g. after logo upload)
  const refreshUser = async () => {
    return await fetchUser();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setAuth, token, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

